import { useState } from 'react';
import { useSimulationStore } from '../../store/simulationStore';
import RunSimulationDialog from './RunSimulationDialog';

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className ?? 'h-4 w-4 shrink-0'}
      aria-hidden
    >
      <path d="M6.3 2.841A1.5 1.5 0 0 0 4 4.11V15.89a1.5 1.5 0 0 0 2.3 1.269l9.344-5.89a1.5 1.5 0 0 0 0-2.538L6.3 2.84Z" />
    </svg>
  );
}

function StopIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className ?? 'h-4 w-4 shrink-0'}
      aria-hidden
    >
      <path d="M4.25 5A2.25 2.25 0 0 0 2 7.25v5.5A2.25 2.25 0 0 0 4.25 15h11.5A2.25 2.25 0 0 0 18 12.75v-5.5A2.25 2.25 0 0 0 15.75 5H4.25Z" />
    </svg>
  );
}

export default function RunSimulationButton({
  projectId,
  disabled = false,
  size = 'default',
  className = '',
}: {
  projectId: string | null | undefined;
  disabled?: boolean;
  size?: 'default' | 'compact';
  className?: string;
}) {
  const runSimulation = useSimulationStore((s) => s.runSimulation);
  const cancelSimulation = useSimulationStore((s) => s.cancelSimulation);
  const runStatus = useSimulationStore((s) => s.run.status);
  const setBottomPanelOpen = useSimulationStore((s) => s.setBottomPanelOpen);
  const setBottomPanelTab = useSimulationStore((s) => s.setBottomPanelTab);

  const [dialogOpen, setDialogOpen] = useState(false);

  const isRunning = runStatus === 'running';

  const sizeClasses =
    size === 'compact'
      ? 'px-3 py-1.5 text-xs'
      : 'px-5 py-2.5 text-sm shadow-brand';

  const startRun = () => {
    if (!projectId) return;
    setBottomPanelOpen(true);
    setBottomPanelTab('results');
    void runSimulation(projectId);
  };

  const handleStop = () => {
    void cancelSimulation();
  };

  if (isRunning) {
    return (
      <button
        type="button"
        className={`btn-danger gap-2 font-semibold ${sizeClasses} ${className}`}
        title="Stop the running simulation"
        onClick={handleStop}
      >
        <StopIcon className={size === 'compact' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        Stop simulation
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`btn-primary gap-2 font-semibold ${sizeClasses} ${className}`}
        disabled={disabled || !projectId}
        title="Configure and run simulation"
        onClick={() => setDialogOpen(true)}
      >
        <PlayIcon className={size === 'compact' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        Run simulation
      </button>
      {projectId && (
        <RunSimulationDialog
          open={dialogOpen}
          projectId={projectId}
          onClose={() => setDialogOpen(false)}
          onRun={startRun}
        />
      )}
    </>
  );
}
